import { Alert, Box, Stack, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { SelectElement } from "react-hook-form-mui";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { GqlMedia, GqlPlannerAreaMediaAudience } from "~/__generated__";
import { GqlPlannerV2AreaStatus } from "~/__generated__/graphql/types";
import GaiaDeleteButton from "~/modules/app/components/GaiaDeleteButton";
import GaiaHookFormSubmitButton from "~/modules/app/components/GaiaHookFormSubmitButton";
import useApplyErrorToForm from "~/modules/app/hooks/useApplyErrorToForm";
import MediaPreviewDialog from "~/modules/media/components/MediaPreviewDialog/MediaPreviewDialog";

import { AddProtocolToPlannerAreaModal } from "./AddProtocolToPlannerAreaModal";
import { MapEditorPlannerAreaPanel_SettingsFormFragment } from "./fragments.generated";
import PlannerAreaAudienceSpecificSection from "./PlannerAreaAudienceSpecificSection";
import { useAddPlannerAreaMediaMutation } from "./useAddPlannerAreaMediaMutation";
import { useDeletePlannerAreaMediaMutation } from "./useDeletePlannerAreaMediaMutation";
import { useDeletePlannerAreaMutation } from "./useDeletePlannerAreaMutation";
import { useRemovePlannerAreaProtocolMutation } from "./useRemovePlannerAreaProtocolMutation";
import { useSavePlannerAreaProtocolMutation } from "./useSavePlannerAreaProtocolMutation";
import { PlannerAreaSettingsFormData, useUpdatePlannerAreaSettingsMutation } from "./useUpdatePlannerAreaSettingsMutation";

type PlannerAreaSettingsFormProps = {
    plannerArea: MapEditorPlannerAreaPanel_SettingsFormFragment;
};

export function PlannerAreaSettingsForm({ plannerArea }: PlannerAreaSettingsFormProps) {
    const { t } = useTranslation("mapEditor");
    const navigate = useNavigate();
    const [selectedItem, setSelectedItem] = useState<GqlMedia | null>(null);

    const [updatePlannerAreaSettings, { error: settingsError }] = useUpdatePlannerAreaSettingsMutation();
    const [addPlannerAreaMedia] = useAddPlannerAreaMediaMutation();
    const [deletePlannerAreaMedia] = useDeletePlannerAreaMediaMutation();
    const [savePlannerAreaProtocol, { error: addProtocolError }] = useSavePlannerAreaProtocolMutation();
    const [removePlannerAreaProtocol, { loading: isDeletingProtocol }] = useRemovePlannerAreaProtocolMutation();
    const [deletePlannerArea, { loading: isDeleting }] = useDeletePlannerAreaMutation();

    const handleDeleteFeature = async () => {
        await deletePlannerArea(plannerArea.featureId);
        navigate(-1);
    };

    const { control, handleSubmit, register, setError } = useForm<PlannerAreaSettingsFormData>({
        values: {
            name: plannerArea.name,
            status: plannerArea.plannerAreaStatus,
            descriptionClient: plannerArea.descriptionClient ?? null,
            descriptionFieldworker: plannerArea.descriptionFieldworker ?? null,
            descriptionInternal: plannerArea.descriptionInternal ?? null,
        },
        disabled: isDeleting,
    });

    const handleDeleteMediaClick = async (file: { id: string }) => {
        await deletePlannerAreaMedia(plannerArea.id, file.id);
        setSelectedItem(null);
    };

    useApplyErrorToForm(settingsError, setError);

    const statusOptions = Object.values(GqlPlannerV2AreaStatus).map(value => ({
        id: value,
        label: t(`featureDetailAreaSettings.${value}StatusLabel`),
    }));

    return (
        <Stack padding={2} sx={{ opacity: isDeleting ? 0.5 : 1 }}>
            <form onSubmit={handleSubmit(data => updatePlannerAreaSettings(plannerArea.id, data))}>
                <Stack gap={2}>
                    {settingsError && (
                        <Alert severity="error">
                            <Typography>{settingsError.message}</Typography>
                        </Alert>
                    )}
                    <TextField {...register("name")} label={t("featureDetailAreaSettings.labelName")} />
                    <SelectElement
                        control={control}
                        disabled={!plannerArea.allowedActions.changeStatus.allowed}
                        helperText={plannerArea.allowedActions.changeStatus.reason}
                        label={t("featureDetailAreaSettings.labelStatus")}
                        rules={{ required: true }}
                        name="status"
                        valueKey="id"
                        options={statusOptions}
                    />
                    <PlannerAreaAudienceSpecificSection
                        title={t("featureDetailAreaSettings.clientSectionTitle")}
                        caption={t("featureDetailAreaSettings.clientSectionCaption")}
                        descriptionField={
                            <TextField
                                {...register("descriptionClient")}
                                label={t("featureDetailAreaSettings.labelDescriptionClient")}
                                multiline
                                minRows={4}
                            />
                        }
                        medias={plannerArea.mediasClient.data ?? []}
                        onUploadFinished={files =>
                            addPlannerAreaMedia(
                                plannerArea.id,
                                files.map(x => x.id),
                                GqlPlannerAreaMediaAudience.Client,
                            )
                        }
                        onMediaClick={media => setSelectedItem(media)}
                    />

                    <PlannerAreaAudienceSpecificSection
                        title={t("featureDetailAreaSettings.fieldworkerSectionTitle")}
                        caption={t("featureDetailAreaSettings.fieldworkerSectionCaption")}
                        descriptionField={
                            <TextField
                                {...register("descriptionFieldworker")}
                                label={t("featureDetailAreaSettings.labelDescriptionFieldworker")}
                                multiline
                                minRows={4}
                            />
                        }
                        medias={plannerArea.mediasFieldworker.data ?? []}
                        onUploadFinished={files =>
                            addPlannerAreaMedia(
                                plannerArea.id,
                                files.map(x => x.id),
                                GqlPlannerAreaMediaAudience.Fieldworker,
                            )
                        }
                        onMediaClick={media => setSelectedItem(media)}
                    />
                    <PlannerAreaAudienceSpecificSection
                        title={t("featureDetailAreaSettings.internalSectionTitle")}
                        caption={t("featureDetailAreaSettings.internalSectionCaption")}
                        descriptionField={
                            <TextField
                                {...register("descriptionInternal")}
                                label={t("featureDetailAreaSettings.labelDescriptionInternal")}
                                multiline
                                minRows={4}
                            />
                        }
                        medias={plannerArea.mediasInternal.data ?? []}
                        onUploadFinished={files =>
                            addPlannerAreaMedia(
                                plannerArea.id,
                                files.map(x => x.id),
                                GqlPlannerAreaMediaAudience.Internal,
                            )
                        }
                        onMediaClick={media => setSelectedItem(media)}
                    />

                    <MediaPreviewDialog
                        media={selectedItem}
                        open={selectedItem !== null}
                        onClose={() => setSelectedItem(null)}
                        onDelete={handleDeleteMediaClick}
                    />

                    <GaiaHookFormSubmitButton control={control}>
                        {t("featureDetailAreaSettings.btnSave")}
                    </GaiaHookFormSubmitButton>
                </Stack>
            </form>

            <Stack>
                <Typography variant="h4">{t("featureDetailAreaSettings.protocols")}</Typography>
                {plannerArea.protocols.map(element => (
                    <Stack key={element.protocol.id} direction="row" gap={1} alignItems="center">
                        <Typography flex={1}>{element.protocol.name}</Typography>

                        <GaiaDeleteButton
                            onDelete={() => removePlannerAreaProtocol(plannerArea.id, element.protocol.id)}
                            isLoading={isDeletingProtocol}
                        />
                    </Stack>
                ))}
            </Stack>

            <AddProtocolToPlannerAreaModal
                onAddProtocolHandler={async protocolId => {
                    await savePlannerAreaProtocol(plannerArea.id, protocolId);
                }}
                addProtocolError={addProtocolError}
            />

            <Box sx={{ display: "flex", justifyContent: "center", padding: 2 }}>
                <GaiaDeleteButton
                    label={t("MapEditorFeatureDetails.featureDelete.btnDelete")}
                    isLoading={isDeleting}
                    onDelete={handleDeleteFeature}
                    sx={{ flex: 1 }}
                />
            </Box>
        </Stack>
    );
}
